import React from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { errorMessage, isPlaceholderEmail, type Achievement, type OnboardingStepKey } from "@atlas/shared";
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  ChevronLeft,
  Circle,
  ListChecks,
  MountainSnow,
} from "lucide-react";
import {
  fetchAdminUser,
  fetchAdminUserAchievements,
  fetchAdminUserCollected,
  fetchAdminUserCollections,
  fetchAdminUserOverview,
  type AdminUserOverview,
} from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ProgressBar } from "../../components/ProgressBar";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { OpenShowcaseButton } from "./AdminUserActivitiesPage";
import { Segmented, formatRelative } from "./usage-ui";

// Read-only support view of one user's Atlas: what they have collected, where
// they are on each collection and achievement, and whether anything is stuck.
// Every figure comes from the same server function the user's own screens use.

type Section = "overview" | "collections" | "achievements" | "collected";

const SECTIONS: { value: Section; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "collections", label: "Collections" },
  { value: "achievements", label: "Achievements" },
  { value: "collected", label: "Collected" },
];

const STEP_LABEL: Record<OnboardingStepKey, string> = {
  verify_email: "Verify email",
  connect_source: "Connect a source",
  first_activity_processed: "First activity processed",
  first_unlock: "First find",
  set_up_profile: "Choose a username",
  find_explorers: "Follow an explorer",
};

// ─── Small building blocks ────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-lg)",
  boxShadow: "var(--shadow-sm), var(--ring-top)",
  padding: "14px 18px",
  minWidth: 0,
};

function Card({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <p className="eyebrow" style={{ fontSize: 10, margin: 0 }}>{title}</p>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children, warn }: { label: string; children: React.ReactNode; warn?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "6px 0",
        borderBottom: "1px solid var(--border-subtle)",
        fontSize: 13,
      }}
    >
      <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>{label}</span>
      <span
        className="tnum"
        style={{
          color: warn ? "var(--status-warning)" : "var(--text-primary)",
          textAlign: "right",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </span>
    </div>
  );
}

function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div style={{ ...card, padding: "12px 14px" }}>
      <div className="tnum" style={{ font: "var(--type-h3)", color: "var(--text-primary)" }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Loading() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Skel height={60} />
      <Skel height={160} />
      <Skel height={160} />
    </div>
  );
}

function LoadError({ error }: { error: unknown }) {
  return (
    <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
      {errorMessage(error, "Failed to load")}
    </p>
  );
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString() : "-";
}

const fmt = (n: number) => n.toLocaleString();

// Public pages open in a new tab so the admin view stays put.
function PublicLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ color: "var(--text-primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
    >
      {children}
    </a>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

function healthWarnings(o: AdminUserOverview): string[] {
  const out: string[] = [];
  const h = o.health;
  if (h.pendingActivities > 0) {
    const old = h.oldestPendingAt && Date.now() - new Date(h.oldestPendingAt).getTime() > 30 * 60_000;
    out.push(
      `${fmt(h.pendingActivities)} activit${h.pendingActivities === 1 ? "y" : "ies"} not processed yet${old ? `, oldest queued ${formatRelative(h.oldestPendingAt)}` : ""}`,
    );
  }
  // sync_runs.status: queued | running | done | error.
  for (const s of h.syncs) {
    const stuck = (s.status === "queued" || s.status === "running")
      && Date.now() - new Date(s.startedAt).getTime() > 60 * 60_000;
    if (s.status === "error") out.push(`Last ${s.provider} sync failed${s.error ? `: ${s.error}` : ""}`);
    else if (stuck) out.push(`${s.provider} sync ${s.status} since ${formatRelative(s.startedAt).toLowerCase()}`);
    else if (s.failedLast30Days > 0) out.push(`${s.failedLast30Days} failed ${s.provider} sync${s.failedLast30Days === 1 ? "" : "s"} in the last 30 days`);
  }
  if (h.historyTruncatedAt) {
    out.push(`Free 12-month window skipped older walks (oldest skipped ${formatDate(h.historyOldestSkippedAt)})`);
  }
  if (o.stats.stats.totalActivities > 0 && h.processedActivities > 0 && o.stats.stats.outdoorScore === 0) {
    out.push("Has processed activities but an Outdoor Score of 0");
  }
  return [...new Set(out)];
}

function OverviewSection({ userId }: { userId: string }) {
  const q = useQuery({
    queryKey: ["admin/users", userId, "overview"],
    queryFn: () => fetchAdminUserOverview(userId),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError error={q.error} />;
  const o = q.data;
  const s = o.stats.stats;
  const warnings = healthWarnings(o);

  return (
    <>
      {/* ── Health ───────────────────────────────────────────── */}
      <section
        style={{
          ...card,
          borderColor: warnings.length > 0 ? "var(--status-warning)" : "var(--border-subtle)",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        {warnings.length > 0 ? (
          <AlertTriangle size={16} style={{ color: "var(--status-warning)", flexShrink: 0, marginTop: 2 }} />
        ) : (
          <CheckCircle2 size={16} style={{ color: "var(--status-success)", flexShrink: 0, marginTop: 2 }} />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
            {warnings.length > 0 ? "Worth a look" : "Nothing looks stuck"}
          </div>
          {warnings.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: "var(--text-secondary)" }}>
              {warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
        </div>
      </section>

      {/* ── Headline stats ───────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
        <Stat value={fmt(s.outdoorScore)} label={`Outdoor score · level ${s.level}`} />
        <Stat value={fmt(s.totalSummits)} label="Summits" />
        <Stat value={fmt(s.totalActivities)} label="Activities" />
        <Stat value={`${fmt(Math.round(s.totalDistanceM / 1000))} km`} label="Distance" />
        <Stat value={`${fmt(Math.round(s.totalElevationGainM))} m`} label="Ascent" />
        <Stat value={fmt(s.totalDaysOut)} label="Days out" />
        <Stat value={fmt(o.social.exploredCells)} label="Explored cells" />
        <Stat value={fmt(o.stats.waypoints)} label="Waypoints" />
      </div>

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
        {/* ── Getting started ──────────────────────────────── */}
        <Card
          title="Getting started"
          aside={
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {o.onboarding.completedAt ? `Finished ${formatDate(o.onboarding.completedAt)}` : "Not finished"}
            </span>
          }
        >
          {o.onboarding.steps.map((step) => (
            <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 13 }}>
              {step.done ? (
                <CheckCircle2 size={14} style={{ color: "var(--status-success)", flexShrink: 0 }} />
              ) : (
                <Circle size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
              )}
              <span style={{ color: step.done ? "var(--text-primary)" : "var(--text-secondary)" }}>
                {STEP_LABEL[step.key] ?? step.key}
                {step.optional && <span style={{ color: "var(--text-muted)" }}> (optional)</span>}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>
                {step.detail ?? (step.doneAt ? formatDate(step.doneAt) : "")}
              </span>
            </div>
          ))}
        </Card>

        {/* ── Import health ────────────────────────────────── */}
        <Card title="Imports">
          <Row label="Processed">{fmt(o.health.processedActivities)}</Row>
          <Row label="Waiting to process" warn={o.health.pendingActivities > 0}>{fmt(o.health.pendingActivities)}</Row>
          {o.health.syncs.length === 0 ? (
            <Row label="Source syncs">None run</Row>
          ) : (
            o.health.syncs.map((sync) => (
              <Row key={sync.provider} label={`Last ${sync.provider} sync`} warn={sync.status === "error"}>
                <span title={sync.error ?? undefined}>
                  {sync.status} · {formatRelative(sync.finishedAt ?? sync.startedAt)} · {fmt(sync.importedCount)} in, {fmt(sync.skippedCount)} skipped
                </span>
              </Row>
            ))
          )}
          <Row label="Skipped kinds">
            {o.health.excludedActivityKinds.length > 0 ? o.health.excludedActivityKinds.join(", ") : "None"}
          </Row>
          {Object.entries(o.health.exclusions).map(([reason, count]) => (
            <Row key={reason} label={reason === "removed" ? "Removed by user" : "Skipped by preference"}>{fmt(count)}</Row>
          ))}
          <Row label="History window" warn={!!o.health.historyTruncatedAt && !o.health.fullHistoryAt}>
            {o.health.fullHistoryAt
              ? `Full history (${o.health.fullHistorySource ?? "unknown"})`
              : o.health.historyTruncatedAt
                ? `12 months, older walks skipped`
                : "12 months, nothing skipped"}
          </Row>
          <Row label="Push notifications">
            {o.health.pushPlatforms.length > 0
              ? o.health.pushPlatforms.map((p) => p.platform).join(", ")
              : "Not registered"}
          </Row>
        </Card>

        {/* ── Profile and social ───────────────────────────── */}
        <Card title="Profile and social">
          <Row label="Username">{o.profile.username ? `@${o.profile.username}` : "Not set"}</Row>
          <Row label="Profile">{o.profile.profileVisibility}</Row>
          <Row label="Streak">
            {o.stats.streaks.currentStreak} current, {o.stats.streaks.longestStreak} longest
          </Row>
          <Row label="Posts">{fmt(o.social.posts)}</Row>
          <Row label="Followers / following">{fmt(o.social.followers)} / {fmt(o.social.following)}</Row>
          <Row label="Photo check-ins">{fmt(o.social.checkins)}</Row>
          <Row label="Goals">{fmt(o.social.goalsOpen)} open, {fmt(o.social.goalsDone)} done</Row>
          <Row label="Events completed">{fmt(o.social.eventsDone)}</Row>
          <Row label="Feedback sent">{fmt(o.social.feedback)}</Row>
          <Row label="Groups">
            {o.social.groups.length > 0
              ? o.social.groups.map((g) => `${g.name}${g.role !== "member" ? ` (${g.role})` : ""}`).join(", ")
              : "None"}
          </Row>
        </Card>

        {/* ── Cosmetics and attribution ────────────────────── */}
        <Card title="Cosmetics and attribution">
          <Row label="Items owned">{fmt(o.social.inventory)}</Row>
          <Row label="Title">{o.stats.equipped.title?.name ?? "None"}</Row>
          <Row label="Banner">{o.stats.equipped.banner?.name ?? "None"}</Row>
          <Row label="Badges shown">
            {o.stats.equipped.badges.length > 0 ? o.stats.equipped.badges.map((b) => b.name).join(", ") : "None"}
          </Row>
          <Row label="Map theme">{o.stats.equipped.mapTheme?.name ?? "Default"}</Row>
          <Row label="Invited by">
            {o.attribution.referredBy
              ? o.attribution.referredBy.username
                ? `@${o.attribution.referredBy.username}`
                : o.attribution.referredBy.name
              : "Nobody"}
          </Row>
          <Row label="Partner code">
            {o.attribution.partner
              ? `${o.attribution.partner.code}${o.attribution.partner.label ? ` (${o.attribution.partner.label})` : ""}`
              : "None"}
          </Row>
        </Card>
      </div>

      {o.stats.recentUnlocks.length > 0 && (
        <Card title="Latest achievements">
          {o.stats.recentUnlocks.map((u) => (
            <Row key={u.achievementId} label={u.name}>
              {u.tier} · {formatDate(u.unlockedAt)}
            </Row>
          ))}
        </Card>
      )}
    </>
  );
}

// ─── Collections ─────────────────────────────────────────────────────────────

function CollectionsSection({ userId }: { userId: string }) {
  const [showAll, setShowAll] = React.useState(false);
  const q = useQuery({
    queryKey: ["admin/users", userId, "collections"],
    queryFn: () => fetchAdminUserCollections(userId),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError error={q.error} />;

  const all = q.data.collections;
  const started = all.filter((c) => c.pct > 0);
  const rows = showAll ? all : started;
  const finished = all.filter((c) => c.itemCount > 0 && c.completedCount === c.itemCount).length;

  return (
    <Card
      title={`Collections · ${started.length} started, ${finished} finished, of ${all.length}`}
      aside={
        <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Started only" : "Show all"}
        </Button>
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon={<ListChecks size={20} />} title="No collections started" hint="Nothing on the record in any collection yet." />
      ) : (
        rows.map((c) => (
          <div
            key={c.id}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr) 140px",
              gap: 14,
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "1px solid var(--border-subtle)",
              fontSize: 13,
            }}
          >
            <PublicLink href={c.trailSlug ? `/trails/${c.trailSlug}` : `/collections/${c.slug}`}>{c.name}</PublicLink>
            <ProgressBar value={c.pct} max={100} size="sm" showValue={false} color={c.pct >= 100 ? "gold" : "sky"} />
            <span className="tnum" style={{ textAlign: "right", color: "var(--text-secondary)", fontSize: 12 }}>
              {c.walkedKm != null && c.distanceKm != null
                ? `${c.walkedKm} / ${Math.round(c.distanceKm)} km`
                : `${c.completedCount} / ${c.itemCount}`}
              {" · "}
              {Math.round(c.pct)}%
            </span>
          </div>
        ))
      )}
    </Card>
  );
}

// ─── Achievements ────────────────────────────────────────────────────────────

const TIER_VARIANT: Record<string, "gold" | "info" | "neutral" | "success"> = {
  gold: "gold",
  platinum: "gold",
  silver: "info",
  bronze: "neutral",
};

function AchievementRow({ a }: { a: Achievement }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 2fr) 120px",
        gap: 14,
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid var(--border-subtle)",
        fontSize: 13,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {a.description}
        </div>
      </div>
      {a.unlocked ? (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Earned {formatDate(a.unlockedAt)}</span>
      ) : a.progress ? (
        <ProgressBar
          value={a.progress.value}
          max={a.progress.max}
          size="sm"
          valueFormat={(v, m) => `${v} / ${m}`}
          color="sky"
        />
      ) : (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Not started</span>
      )}
      <span style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <Badge variant={TIER_VARIANT[a.tier] ?? "neutral"}>{a.tier}</Badge>
        <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>{a.points} pts</span>
      </span>
    </div>
  );
}

function AchievementsSection({ userId }: { userId: string }) {
  const [filter, setFilter] = React.useState<"earned" | "progress" | "all">("earned");
  const q = useQuery({
    queryKey: ["admin/users", userId, "achievements"],
    queryFn: () => fetchAdminUserAchievements(userId),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError error={q.error} />;

  const all = q.data.achievements;
  const earned = all.filter((a) => a.unlocked);
  const inProgress = all.filter((a) => !a.unlocked && a.progress);
  const rows = filter === "earned" ? earned : filter === "progress" ? inProgress : all;
  const points = earned.reduce((sum, a) => sum + a.points, 0);

  return (
    <Card
      title={`Achievements · ${earned.length} of ${all.length} earned, ${fmt(points)} points`}
      aside={
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "earned", label: `Earned ${earned.length}` },
            { value: "progress", label: `In progress ${inProgress.length}` },
            { value: "all", label: "All" },
          ]}
        />
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon={<Award size={20} />} title="Nothing here" hint="No achievements in this view yet." />
      ) : (
        rows.map((a) => <AchievementRow key={a.id} a={a} />)
      )}
    </Card>
  );
}

// ─── Collected ───────────────────────────────────────────────────────────────

function CollectedSection({ userId }: { userId: string }) {
  const q = useQuery({
    queryKey: ["admin/users", userId, "collected"],
    queryFn: () => fetchAdminUserCollected(userId),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <LoadError error={q.error} />;
  const d = q.data;

  const listRow: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "6px 0",
    borderBottom: "1px solid var(--border-subtle)",
    fontSize: 13,
    minWidth: 0,
  };
  const meta: React.CSSProperties = { fontSize: 12, color: "var(--text-muted)", flexShrink: 0, textAlign: "right" };
  const scroll: React.CSSProperties = { maxHeight: 420, overflowY: "auto" };
  const more = (shown: number, total: number) =>
    total > shown ? <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 0" }}>Latest {shown} of {fmt(total)}</p> : null;
  const activityLink = (activityId: string | null, label: string) =>
    activityId ? (
      <Link to="/admin/users/$userId/activities/$activityId" params={{ userId, activityId }} style={{ color: "var(--text-muted)" }}>
        {label}
      </Link>
    ) : (
      <span title="Photo check-in or activity removed">{label}</span>
    );

  return (
    <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
      <Card title={`Peaks · ${fmt(d.peaks.total)}`}>
        {d.peaks.items.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>None yet</p>
        ) : (
          <div style={scroll}>
            {d.peaks.items.map((p) => (
              <div key={p.id} style={listRow}>
                <PublicLink href={`/peaks/${p.slug}`}>{p.name} <span style={{ color: "var(--text-muted)" }}>{p.elevationM} m</span></PublicLink>
                <span style={meta} className="tnum">
                  {p.visits > 1 ? `${p.visits} visits · ` : ""}
                  {activityLink(p.activityId, formatDate(p.collectedAt))}
                </span>
              </div>
            ))}
          </div>
        )}
        {more(d.peaks.items.length, d.peaks.total)}
      </Card>

      <Card title={`Landmarks · ${fmt(d.landmarks.total)}`}>
        {d.landmarks.items.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>None yet</p>
        ) : (
          <div style={scroll}>
            {d.landmarks.items.map((l) => (
              <div key={l.id} style={listRow}>
                <PublicLink href={`/landmarks/${l.slug}`}>{l.name} <span style={{ color: "var(--text-muted)" }}>{l.category.replace(/_/g, " ")}</span></PublicLink>
                <span style={meta} className="tnum">
                  {l.visits > 1 ? `${l.visits} visits · ` : ""}
                  {activityLink(l.activityId, formatDate(l.collectedAt))}
                </span>
              </div>
            ))}
          </div>
        )}
        {more(d.landmarks.items.length, d.landmarks.total)}
      </Card>

      <Card title={`Trails · ${d.trails.length} started`}>
        {d.trails.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>None yet</p>
        ) : (
          <div style={scroll}>
            {d.trails.map((t) => (
              <div key={t.id} style={{ ...listRow, display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 2fr) 120px", alignItems: "center" }}>
                <PublicLink href={`/trails/${t.slug}`}>{t.name}</PublicLink>
                <ProgressBar value={t.pct} max={100} size="sm" showValue={false} color={t.pct >= 100 ? "gold" : "spruce"} />
                <span style={meta} className="tnum">
                  {t.pct}% · {t.sectionsDone}/{t.sections} sections
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Regions · ${d.regions.length} explored`}>
        {d.regions.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>None yet</p>
        ) : (
          <div style={scroll}>
            {d.regions.map((r) => (
              <div key={r.id} style={listRow}>
                <PublicLink href={`/regions/${r.slug}`}>{r.name}</PublicLink>
                <span style={meta} className="tnum">{r.coveragePct.toFixed(r.coveragePct < 1 ? 2 : 1)}%</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminUserProfilePage() {
  const { user: me } = useAuth();
  const { userId } = useParams({ strict: false }) as { userId: string };
  const [section, setSection] = React.useState<Section>("overview");
  const navigate = useNavigate();

  const userQuery = useQuery({
    queryKey: ["admin/users", userId],
    queryFn: () => fetchAdminUser(userId),
    enabled: !!me?.isAdmin && !!userId,
  });

  if (!me?.isAdmin) return <AccessDenied />;
  const target = userQuery.data?.user;

  return (
    <AdminShell
      title="Users"
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<MountainSnow size={14} />}
            onClick={() => void navigate({ to: "/admin/users/$userId/activities", params: { userId } })}
          >
            Activities{target ? ` (${fmt(target.activityCount)})` : ""}
          </Button>
          <OpenShowcaseButton userId={userId} />
        </div>
      }
    >
      <div>
        <Link
          to="/admin/users"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: "var(--text-secondary)",
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          <ChevronLeft size={16} /> All users
        </Link>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>{target?.name ?? "Explorer"}</h2>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
            {target ? (isPlaceholderEmail(target.email) ? "No email yet" : target.email) : ""}
          </span>
          {target && (
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }} className="tnum">
              Joined {formatDate(target.createdAt)} · seen {formatRelative(target.lastSeenAt).toLowerCase()} · {target.plan}
            </span>
          )}
        </div>
      </div>

      <div className="r-scroll-x">
        <Segmented value={section} onChange={setSection} options={SECTIONS} />
      </div>

      {section === "overview" && <OverviewSection userId={userId} />}
      {section === "collections" && <CollectionsSection userId={userId} />}
      {section === "achievements" && <AchievementsSection userId={userId} />}
      {section === "collected" && <CollectedSection userId={userId} />}
    </AdminShell>
  );
}
